"use client"

/**
 * Demo state — orthogonal edge cases for Add question, Assessment Questions,
 * and Blueprint. Reviewers pick each axis from the profile menu; surfaces read
 * the composed config instead of faking data.
 *
 * Persisted shell-global via `usePersistedState`.
 */

import * as React from "react"
import { usePersistedState } from "@exxatdesignux/ui/lib/persisted-state"

import {
  isPbiReviewThreshold,
  normalizePbiReviewThreshold,
  PBI_REVIEW_FLOOR_DEFAULT,
  PBI_REVIEW_FLOOR_PROGRAM,
  type PbiReviewThreshold,
} from "@/lib/pbi-review-floor"
import { assessmentKpiTargets } from "@/lib/assessment-detail-kpis"

export const DEMO_STATE_KEY = "shell:demo-state:v2"
const DEMO_STATE_KEY_V1 = "shell:demo-state:v1"

export type DemoBlueprintId = "full" | "partial" | "none" | "content"

/** Add Questions flow — bank roster and import behaviour. */
export type DemoAddQuestionId =
  | "default"
  | "empty-bank"
  | "import-fails"
  | "import-no-data"
  | "import-empty"

/** Assessment Questions tab — paper shape and item-analysis richness. */
export type DemoAssessmentPaperId =
  | "default"
  | "no-questions"
  | "empty-section"
  | "no-item-stats"

export interface DemoExamPlanState {
  markTotal: number
  plannedQuestions: number
  sittingMinutes: number
  pbiReviewThreshold?: PbiReviewThreshold
}

export interface DemoStateConfig {
  blueprint: DemoBlueprintId
  addQuestion: DemoAddQuestionId
  assessmentPaper: DemoAssessmentPaperId
  /** Saved from the assessment exam plan sheet. */
  examPlan?: DemoExamPlanState
}

/** Item-analysis richness for assessment KPIs and question stats. */
export type DemoMetricsMode = "all_data" | "partial_data" | "no_data"

export interface DemoStateOption<T extends string = string> {
  id: T
  label: string
  hint: string
}

export const DEMO_BLUEPRINT_OPTIONS: readonly DemoStateOption<DemoBlueprintId>[] =
  [
    {
      id: "full",
      label: "Full blueprint",
      hint: "Planned questions, marks, and sitting time set",
    },
    {
      id: "partial",
      label: "Partial blueprint",
      hint: "Blueprint started; finish targets on Questions",
    },
    {
      id: "none",
      label: "No blueprint",
      hint: "No targets; KPI strip shows actuals only",
    },
    {
      id: "content",
      label: "Content matrix",
      hint: "Full exam plan plus content coverage matrix on Blueprint tab",
    },
  ]

export const DEMO_ADD_QUESTION_OPTIONS: readonly DemoStateOption<
  Extract<DemoAddQuestionId, "default" | "empty-bank">
>[] = [
  {
    id: "default",
    label: "Populated bank",
    hint: "Full mock bank to pick from",
  },
  {
    id: "empty-bank",
    label: "Empty bank",
    hint: "No questions to pick from yet",
  },
]

export const DEMO_IMPORT_OPTIONS: readonly DemoStateOption<
  Extract<DemoAddQuestionId, "import-fails" | "import-no-data" | "import-empty">
>[] = [
  {
    id: "import-fails",
    label: "Failed",
    hint: "The read stops partway and the document has to be retried",
  },
  {
    id: "import-no-data",
    label: "No data",
    hint: "The file had no readable question data",
  },
  {
    id: "import-empty",
    label: "Nothing found",
    hint: "Extraction returns no questions",
  },
]

export const DEMO_ASSESSMENT_PAPER_OPTIONS: readonly DemoStateOption<DemoAssessmentPaperId>[] =
  [
    {
      id: "default",
      label: "Populated paper",
      hint: "Mock questions across sections",
    },
    {
      id: "no-questions",
      label: "No questions",
      hint: "Empty paper on the Questions tab",
    },
    {
      id: "empty-section",
      label: "Empty section",
      hint: "One section has no questions yet",
    },
    {
      id: "no-item-stats",
      label: "No item stats",
      hint: "New questions without item analysis yet",
    },
  ]

export const DEFAULT_DEMO_STATE: DemoStateConfig = {
  blueprint: "full",
  addQuestion: "default",
  assessmentPaper: "default",
}

/** @deprecated Flat demo ids — migrated to {@link DemoStateConfig} on read. */
export type DemoStateId =
  | "populated"
  | "partial-blueprint"
  | "full-blueprint"
  | "empty-bank"
  | "import-empty"
  | "import-fails"
  | "no-blueprint"
  | "no-data"

const LEGACY_DEMO_STATE_MAP: Record<DemoStateId, DemoStateConfig> = {
  populated: DEFAULT_DEMO_STATE,
  "partial-blueprint": {
    ...DEFAULT_DEMO_STATE,
    blueprint: "partial",
  },
  "full-blueprint": DEFAULT_DEMO_STATE,
  "empty-bank": {
    ...DEFAULT_DEMO_STATE,
    addQuestion: "empty-bank",
  },
  "import-empty": {
    ...DEFAULT_DEMO_STATE,
    addQuestion: "import-empty",
  },
  "import-fails": {
    ...DEFAULT_DEMO_STATE,
    addQuestion: "import-fails",
  },
  "no-blueprint": {
    ...DEFAULT_DEMO_STATE,
    blueprint: "none",
  },
  "no-data": {
    ...DEFAULT_DEMO_STATE,
    assessmentPaper: "no-item-stats",
  },
}

function isDemoBlueprintId(value: unknown): value is DemoBlueprintId {
  return DEMO_BLUEPRINT_OPTIONS.some((option) => option.id === value)
}

function isDemoAddQuestionId(value: unknown): value is DemoAddQuestionId {
  return (
    value === "default" ||
    value === "empty-bank" ||
    value === "import-fails" ||
    value === "import-no-data" ||
    value === "import-empty"
  )
}

function isDemoAssessmentPaperId(
  value: unknown,
): value is DemoAssessmentPaperId {
  return DEMO_ASSESSMENT_PAPER_OPTIONS.some((option) => option.id === value)
}

function normalizeNonNegativeInt(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0
  return Math.max(0, Math.trunc(value))
}

function normalizeExamPlan(
  raw: Partial<DemoExamPlanState> | undefined,
): DemoExamPlanState | undefined {
  if (!raw || typeof raw !== "object") return undefined
  const markTotal = normalizeNonNegativeInt(raw.markTotal)
  const plannedQuestions = normalizeNonNegativeInt(raw.plannedQuestions)
  const sittingMinutes = normalizeNonNegativeInt(raw.sittingMinutes)
  const pbiReviewThreshold =
    typeof raw.pbiReviewThreshold === "string" &&
    isPbiReviewThreshold(raw.pbiReviewThreshold)
      ? raw.pbiReviewThreshold
      : undefined
  if (
    markTotal === 0 &&
    plannedQuestions === 0 &&
    sittingMinutes === 0 &&
    !pbiReviewThreshold
  ) {
    return undefined
  }
  return {
    markTotal,
    plannedQuestions,
    sittingMinutes,
    ...(pbiReviewThreshold ? { pbiReviewThreshold } : {}),
  }
}

function normalizeDemoState(value: unknown): DemoStateConfig {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Partial<DemoStateConfig>
    const examPlan = normalizeExamPlan(record.examPlan)
    return {
      blueprint: isDemoBlueprintId(record.blueprint)
        ? record.blueprint
        : DEFAULT_DEMO_STATE.blueprint,
      addQuestion: isDemoAddQuestionId(record.addQuestion)
        ? record.addQuestion
        : DEFAULT_DEMO_STATE.addQuestion,
      assessmentPaper: isDemoAssessmentPaperId(record.assessmentPaper)
        ? record.assessmentPaper
        : DEFAULT_DEMO_STATE.assessmentPaper,
      ...(examPlan ? { examPlan } : {}),
    }
  }
  if (
    typeof value === "string" &&
    value in LEGACY_DEMO_STATE_MAP
  ) {
    return LEGACY_DEMO_STATE_MAP[value as DemoStateId]
  }
  return DEFAULT_DEMO_STATE
}

/** Maps demo config to assessment item-analysis mode. */
export function demoMetricsModeFromDemoState(
  demoState: DemoStateConfig,
): DemoMetricsMode {
  if (demoState.assessmentPaper === "no-item-stats") return "no_data"
  if (demoState.blueprint === "full") return "all_data"
  return "partial_data"
}

/** Whether the assessment has blueprint topic targets to measure against. */
export function demoHasBlueprintTargets(demoState: DemoStateConfig): boolean {
  return demoState.blueprint !== "none"
}

/** Whether the demo should seed a complete exam plan on load. */
export function demoSeedsFullExamPlan(demoState: DemoStateConfig): boolean {
  return demoState.blueprint === "full" || demoState.blueprint === "content"
}

/** Phase 3 content × competency matrix on the Blueprint tab. */
export function demoContentMatrixEnabled(demoState: DemoStateConfig): boolean {
  return demoState.blueprint === "content"
}

export interface ResolvedDemoExamPlan {
  markTotal: number
  plannedQuestions: number
  sittingMinutes: number
  pbiReviewThreshold: PbiReviewThreshold
}

/** Exam plan targets from saved demo state, blueprint seed, or empty. */
export function resolveDemoExamPlan(
  demoState: DemoStateConfig,
  assessment: { questionCount: number },
): ResolvedDemoExamPlan {
  const empty: ResolvedDemoExamPlan = {
    markTotal: 0,
    plannedQuestions: 0,
    sittingMinutes: 0,
    pbiReviewThreshold: PBI_REVIEW_FLOOR_DEFAULT,
  }

  if (!demoHasBlueprintTargets(demoState)) return empty

  const saved = demoState.examPlan
  if (
    saved &&
    (saved.markTotal > 0 ||
      saved.plannedQuestions > 0 ||
      saved.sittingMinutes > 0 ||
      saved.pbiReviewThreshold)
  ) {
    const threshold = normalizePbiReviewThreshold(saved.pbiReviewThreshold)
    return {
      markTotal: saved.markTotal,
      plannedQuestions: saved.plannedQuestions,
      sittingMinutes: saved.sittingMinutes,
      pbiReviewThreshold: threshold,
    }
  }

  if (demoSeedsFullExamPlan(demoState)) {
    const seeded = assessmentKpiTargets(assessment)
    return {
      markTotal: seeded.targetPoints,
      plannedQuestions: seeded.targetQuestions,
      sittingMinutes: seeded.targetMinutes,
      pbiReviewThreshold: PBI_REVIEW_FLOOR_PROGRAM,
    }
  }

  return empty
}

export function demoBankIsEmpty(demoState: DemoStateConfig): boolean {
  return demoState.addQuestion === "empty-bank"
}

export function demoImportFails(demoState: DemoStateConfig): boolean {
  return demoState.addQuestion === "import-fails"
}

export function demoImportNoData(demoState: DemoStateConfig): boolean {
  return demoState.addQuestion === "import-no-data"
}

export function demoImportYieldsNothing(demoState: DemoStateConfig): boolean {
  return demoState.addQuestion === "import-empty"
}

/** Strip or empty paper rows for Assessment Questions tab demos. */
export function applyDemoAssessmentPaper<
  T extends { sectionId?: string; status?: string },
>(rows: T[], paper: DemoAssessmentPaperId): T[] {
  if (paper === "no-questions") return []
  if (paper === "empty-section") {
    return rows.filter((row) => row.sectionId !== "B")
  }
  return rows
}

export function demoStateSummary(demoState: DemoStateConfig): string {
  const parts: string[] = []
  if (demoState.blueprint !== DEFAULT_DEMO_STATE.blueprint) {
    parts.push(
      DEMO_BLUEPRINT_OPTIONS.find((option) => option.id === demoState.blueprint)
        ?.label ?? demoState.blueprint,
    )
  }
  if (demoState.addQuestion !== DEFAULT_DEMO_STATE.addQuestion) {
    const importOption = DEMO_IMPORT_OPTIONS.find(
      (option) => option.id === demoState.addQuestion,
    )
    const addOption = DEMO_ADD_QUESTION_OPTIONS.find(
      (option) => option.id === demoState.addQuestion,
    )
    parts.push(importOption?.label ?? addOption?.label ?? demoState.addQuestion)
  }
  if (demoState.assessmentPaper !== DEFAULT_DEMO_STATE.assessmentPaper) {
    parts.push(
      DEMO_ASSESSMENT_PAPER_OPTIONS.find(
        (option) => option.id === demoState.assessmentPaper,
      )?.label ?? demoState.assessmentPaper,
    )
  }
  return parts.length > 0 ? parts.join(" · ") : "Default"
}

interface DemoStateContextValue {
  demoState: DemoStateConfig
  setDemoBlueprint: (next: DemoBlueprintId) => void
  setDemoAddQuestion: (next: DemoAddQuestionId) => void
  setDemoAssessmentPaper: (next: DemoAssessmentPaperId) => void
  setDemoExamPlan: (next: DemoExamPlanState) => void
}

const DemoStateContext = React.createContext<DemoStateContextValue>({
  demoState: DEFAULT_DEMO_STATE,
  setDemoBlueprint: () => {},
  setDemoAddQuestion: () => {},
  setDemoAssessmentPaper: () => {},
  setDemoExamPlan: () => {},
})

export function useDemoState() {
  return React.useContext(DemoStateContext)
}

function readLegacyV1State(): DemoStateConfig | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(`exxat-ds:${DEMO_STATE_KEY_V1}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    return normalizeDemoState(parsed)
  } catch {
    return null
  }
}

export function DemoStateProvider({ children }: { children: React.ReactNode }) {
  const [stored, setStored] = usePersistedState<DemoStateConfig>(
    DEMO_STATE_KEY,
    () => readLegacyV1State() ?? DEFAULT_DEMO_STATE,
    { debounceMs: 0 },
  )

  const demoState = normalizeDemoState(stored)

  const patchDemoState = React.useCallback(
    (patch: Partial<DemoStateConfig>) =>
      setStored((current) => ({
        ...normalizeDemoState(current),
        ...patch,
      })),
    [setStored],
  )

  const setDemoBlueprint = React.useCallback(
    (blueprint: DemoBlueprintId) =>
      patchDemoState({
        blueprint,
        ...(blueprint === "none" ? { examPlan: undefined } : {}),
      }),
    [patchDemoState],
  )

  const setDemoAddQuestion = React.useCallback(
    (addQuestion: DemoAddQuestionId) => patchDemoState({ addQuestion }),
    [patchDemoState],
  )

  const setDemoAssessmentPaper = React.useCallback(
    (assessmentPaper: DemoAssessmentPaperId) =>
      patchDemoState({ assessmentPaper }),
    [patchDemoState],
  )

  const setDemoExamPlan = React.useCallback(
    (examPlan: DemoExamPlanState) => {
      const normalized = normalizeExamPlan(examPlan)
      patchDemoState({ examPlan: normalized })
    },
    [patchDemoState],
  )

  const value = React.useMemo(
    () => ({
      demoState,
      setDemoBlueprint,
      setDemoAddQuestion,
      setDemoAssessmentPaper,
      setDemoExamPlan,
    }),
    [
      demoState,
      setDemoBlueprint,
      setDemoAddQuestion,
      setDemoAssessmentPaper,
      setDemoExamPlan,
    ],
  )

  return (
    <DemoStateContext.Provider value={value}>
      {children}
    </DemoStateContext.Provider>
  )
}
